/**
 * Client half of the admin `resetExploreUsage` callable (watchlock PR #1029).
 *
 * The callable clears a user's free-tier explore counter by bumping a
 * `resetVersion` on their user document. The RN client compares the version it
 * has cached against the one it reads on launch/sync, so the reset lands on the
 * device without any push: a higher version means "throw your local counter
 * away". That version number is therefore the only receipt this action produces,
 * and surfacing it back to the admin is how they verify the write actually
 * happened rather than trusting a green toast.
 *
 * TWO ARGUMENT NAMES, deliberately. #1029 accepts `uid` and keeps `targetUid`
 * for compatibility with the older admin callables (`grantAiCredits`,
 * `getUserAiCreditStatus`, which all key on `targetUid`). We send both, because
 * sending one and guessing wrong fails at the server for a reason no toast could
 * explain, and the two always carry the same value here.
 *
 * THE TIMEOUT IS THE POINT OF THIS FILE'S ONLY OPTION. `resetExploreUsage` is a
 * 2nd-gen function whose container takes ~85s to pass its startup probe, and it
 * is a support action nobody calls often enough to keep an instance warm — so
 * essentially every real use is a cold start. Measured on 2026-09-01: the
 * browser's CORS preflight alone blocked for 106s and 108s on two clicks, then
 * the warm call returned in 1.1s. The SDK's default callable timeout is 70s
 * (`options.timeout || 70000` in @firebase/functions), which is SHORTER than
 * that cold start, so the first click aborted before the function ever answered
 * — an error for a reset that then landed anyway. Waiting the cold start out is
 * the only honest option available on this side; the boot time itself is a
 * watchlock question.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface ResetExploreUsageResult {
  /**
   * The user's explore counter version AFTER the reset, when the callable
   * reported one.
   *
   * NULL IS NOT ZERO. Null means the response carried no version — an older
   * deploy, or a shape we do not recognise — where 0 would be a version we had
   * actually read. The reset may well have succeeded in that case, so this is
   * not an error; it just means we cannot show the receipt, and the caller words
   * its success message accordingly instead of printing a fabricated number.
   */
  resetVersion: number | null;
}

/**
 * Pull the new version out of the callable's reply.
 *
 * Written tolerantly on purpose: the field has been seen at the top level and
 * nested under a state object, and this admin button is not worth a redeploy
 * over a key that moved. Anything non-finite (a string, NaN, a missing key)
 * becomes null rather than being coerced — see the note on `resetVersion`.
 */
export function normaliseResetExploreResponse(
  data: unknown,
): ResetExploreUsageResult {
  const version = findResetVersion(data);
  return { resetVersion: version };
}

/** `resetVersion` at the top level, or one level down in the usual wrappers. */
function findResetVersion(data: unknown): number | null {
  if (!data || typeof data !== 'object') return null;

  const top = numberOrNull((data as Record<string, unknown>).resetVersion);
  if (top !== null) return top;

  for (const key of ['newState', 'exploreUsage', 'state', 'result'] as const) {
    const nested = (data as Record<string, unknown>)[key];
    if (nested && typeof nested === 'object') {
      const inner = numberOrNull((nested as Record<string, unknown>).resetVersion);
      if (inner !== null) return inner;
    }
  }

  return null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Admin-only: clear a user's explore usage counter. Super-admin gated
 * server-side, so a non-privileged caller gets an HttpsError rather than a
 * silent no-op. Errors are left to propagate — the caller decides what to say.
 */
export async function resetExploreUsage(
  uid: string,
): Promise<ResetExploreUsageResult> {
  const fn = httpsCallable<{ uid: string; targetUid: string }, unknown>(
    functions,
    'resetExploreUsage',
    // Three minutes, comfortably past the ~110s worst case measured above. The
    // 70s default is the one number guaranteed to be wrong here.
    { timeout: 180_000 },
  );
  const result = await fn({ uid, targetUid: uid });
  return normaliseResetExploreResponse(result.data);
}
